/**
 * Comparação entre execuções.
 *
 * Sobrepõe curvas de concorrência contra latência, com **eixo horizontal
 * logarítmico** — em escala linear a faixa de 1 a 128 comprime justamente o
 * joelho da curva, que é o único ponto de interesse.
 *
 * Execuções marcadas como não comparáveis são excluídas da sobreposição, com a
 * razão informada. Deixá-las entrar produziria uma comparação sem significado
 * cuja invalidade não apareceria no gráfico.
 */

import { useMemo, useState } from "react";
import { Checkbox } from "@cloudflare/kumo";
import { ConcurrencyCurveChart, type SeriesPoint } from "../components/charts.js";
import { Notice } from "../components/Notice.js";
import { Page } from "../components/Page.js";
import { Panel } from "../components/Panel.js";
import { Stat } from "../components/Stat.js";
import { snapshot } from "../data/source.js";
import { FLOOR_TOKENS_PER_SEC, TARGET_TOKENS_PER_SEC } from "../fixtures/scale.js";

/** Uma execução candidata à sobreposição, com o motivo quando indisponível. */
interface Selectable {
  readonly runId: number;
  readonly label: string;
  readonly comparable: boolean;
  readonly reason: string | null;
  readonly pointCount: number;
}

/** Sobreposição de curvas de concorrência entre execuções. */
export function Compare(): React.ReactElement {
  const selectables = useMemo<Selectable[]>(
    () =>
      snapshot.runs.map((run) => ({
        runId: run.id,
        label: `#${run.id} · ${run.instanceType} · ${run.gpuCount}× ${run.gpuModel}`,
        comparable: run.comparable,
        reason: run.notComparableReason,
        pointCount: snapshot.scenarios.filter((s) => s.runId === run.id && s.loadProfile === "chat").length,
      })),
    [],
  );

  // Só entram as execuções comparáveis com mais de um ponto — uma curva de um
  // ponto só não é curva, e sobrepor não diria nada.
  const eligible = selectables.filter((s) => s.comparable && s.pointCount > 1);
  const excluded = selectables.filter((s) => !s.comparable || s.pointCount <= 1);

  const [selected, setSelected] = useState<number[]>(() => eligible.map((s) => s.runId));

  /** Pontos de concorrência contra tokens por segundo, por execução. */
  const curve = useMemo<SeriesPoint[]>(() => {
    const points: SeriesPoint[] = [];
    for (const runId of selected) {
      const run = snapshot.runs.find((r) => r.id === runId);
      if (run === undefined) {
        continue;
      }
      const label = `${run.instanceType} (${run.gpuCount}× ${run.gpuModel})`;
      const scenarios = snapshot.scenarios
        .filter((s) => s.runId === runId && s.loadProfile === "chat" && s.radixCache)
        .sort((a, b) => a.concurrency - b.concurrency);
      for (const scenario of scenarios) {
        const stats = snapshot.stats.find((s) => s.scenarioId === scenario.id);
        if (stats === undefined) {
          continue;
        }
        points.push({ x: scenario.concurrency, y: Number(stats.tokensPerSecP95.toFixed(1)), series: label });
      }
    }
    return points;
  }, [selected]);

  /** Onde cada curva cruza o alvo — o número que decide a compra. */
  const crossings = useMemo(() => {
    const bySeries = new Map<string, SeriesPoint[]>();
    for (const point of curve) {
      bySeries.set(point.series, [...(bySeries.get(point.series) ?? []), point]);
    }
    return [...bySeries.entries()].map(([series, points]) => {
      const sorted = [...points].sort((a, b) => Number(a.x) - Number(b.x));
      const lastMeeting = [...sorted].reverse().find((p) => p.y >= TARGET_TOKENS_PER_SEC);
      return { series, concurrency: lastMeeting === undefined ? null : Number(lastMeeting.x) };
    });
  }, [curve]);

  return (
    <Page>
      <Panel title="Execuções na sobreposição">
        <Checkbox.Group
          legend="execuções comparáveis com mais de um ponto de concorrência"
          value={selected.map(String)}
          onValueChange={(values) => setSelected(values.map(Number))}
        >
          {eligible.map((s) => (
            <Checkbox.Item key={s.runId} label={s.label} value={String(s.runId)} />
          ))}
        </Checkbox.Group>
      </Panel>

      {excluded.map((s) => (
        <Notice
          key={s.runId}
          tone="warning"
          title={`Fora da comparação: ${s.label}`}
          description={
            s.comparable ? "A execução tem apenas um ponto de concorrência; uma curva de um ponto não é curva." : s.reason
          }
        />
      ))}

      <Panel
        title="Concorrência × tokens por segundo"
        description="Eixo horizontal em escala logarítmica. A linha tracejada é o alvo — onde a curva a cruza, o servidor deixa de atender."
      >
        <ConcurrencyCurveChart
          data={curve}
          targetValue={TARGET_TOKENS_PER_SEC}
          targetLabel={`alvo — ${TARGET_TOKENS_PER_SEC} tok/s`}
          yTitle="tokens por segundo por requisição (percentil 95)"
          height={340}
        />
      </Panel>

      <Panel
        title="Concorrência máxima que ainda atende ao alvo"
        bodyClassName="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3"
      >
        {crossings.length === 0 && <p className="text-sm text-kumo-subtle">nenhuma execução selecionada</p>}
        {crossings.map((c) => (
          <Stat
            key={c.series}
            label={c.series}
            value={c.concurrency ?? "não atinge"}
            tone={c.concurrency === null ? "danger" : "success"}
            {...(c.concurrency === null
              ? {
                  caption: `não sustenta o alvo em nenhuma concorrência medida; piso de ${FLOOR_TOKENS_PER_SEC} tok/s ainda pode ser atendido`,
                }
              : { suffix: "sessões" })}
          />
        ))}
      </Panel>
    </Page>
  );
}
